import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent,
  Button
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import TemplateRenderer from '../TemplateRenderers/TemplateRenderer';
import RFQWorkflowDetail from './RFQWorkflowDetail';
import VendorResponsesDashboard from './VendorResponsesDashboard';
import BidAnalysisView from './BidAnalysisView';
import rfqWorkflowService from '../../services/rfqWorkflowService';
import Swal from 'sweetalert2';
import {
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Loader2
} from 'lucide-react';

const RFQWorkflowRun = () => {
  const { runId } = useParams();
  const navigate = useNavigate();

  const [workflowRun, setWorkflowRun] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch workflow status
  const fetchWorkflowStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await rfqWorkflowService.getWorkflowStatus(runId);
      console.log('Workflow status response:', response);

      if (response) {
        setWorkflowRun(response);
        setCurrentStep(response.current_step || null);
      }
    } catch (err) {
      console.error('Error fetching workflow status:', err);
      setError(err.message || 'Failed to fetch workflow status');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  // Initial fetch
  useEffect(() => {
    if (runId) {
      fetchWorkflowStatus();
    }
  }, [runId, fetchWorkflowStatus]);

  // Handle form submission
  const handleFormSubmit = async (formData) => {
    console.log('Form submitted:', formData);
    setSubmitting(true);

    try {
      const stepName = currentStep?.name || 'current_step';
      const message = JSON.stringify({
        action: 'submit',
        step: stepName,
        data: formData
      });

      const response = await rfqWorkflowService.continueWorkflow(runId, message);
      console.log('Continue workflow response:', response);

      if (response?.success) {
        // Check if workflow is now completed
        if (response.status === 'completed') {
          // Show completion message and navigate
          Swal.fire({
            icon: 'success',
            title: 'Workflow Completed',
            text: 'The RFQ workflow has been completed successfully.',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Go to Dashboard'
          }).then(() => {
            navigate('/rfq-workflow');
          });
          return;
        }

        // Fetch updated status for next step
        const statusResponse = await rfqWorkflowService.getWorkflowStatus(runId);
        if (statusResponse) {
          // Check if RFQ was just created - redirect to RFQ detail page
          const context = statusResponse.context || {};
          const rfqId = context.rfq_id || context.netsuite_rfq_result?.rfq_id || response.rfq_id;

          if (stepName === 'create_rfq_ui' && rfqId) {
            Swal.fire({
              icon: 'success',
              title: 'RFQ Created Successfully',
              text: 'Your RFQ has been created and saved to NetSuite.',
              confirmButtonColor: '#3085d6',
              confirmButtonText: 'View RFQ Details'
            }).then(() => {
              navigate(`/rfq-workflow/rfq/${rfqId}`);
            });
            return;
          }

          setWorkflowRun(statusResponse);
          setCurrentStep(statusResponse.current_step || null);
        }
      }
    } catch (err) {
      console.error('Error continuing workflow:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to continue workflow',
        confirmButtonColor: '#d33'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal action button clicks
  const handleActionClick = async (actionName) => {
    console.log('Action clicked:', actionName);
    setSubmitting(true);

    try {
      const stepName = currentStep?.name || 'current_step';
      const message = JSON.stringify({
        action: actionName,
        step: stepName
      });

      const response = await rfqWorkflowService.continueWorkflow(runId, message);
      console.log('Continue workflow response:', response);

      if (response?.success) {
        // Check if workflow is now completed
        if (response.status === 'completed') {
          // Navigate to RFQ detail page if we have rfq_id
          const rfqId = workflowRun?.context?.rfq_id;
          if (rfqId) {
            navigate(`/rfq-workflow/rfq/${rfqId}`);
          } else {
            navigate('/rfq-workflow');
          }
          return;
        }

        // Fetch updated status for next step
        const statusResponse = await rfqWorkflowService.getWorkflowStatus(runId);
        if (statusResponse) {
          setWorkflowRun(statusResponse);
          setCurrentStep(statusResponse.current_step || null);
        }
      }
    } catch (err) {
      console.error('Error continuing workflow:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to continue workflow',
        confirmButtonColor: '#d33'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal close / cancel
  const handleClose = () => {
    navigate('/rfq-workflow');
  };

  // Check if step uses a custom component
  // Check both paths: config.ui_component (flat) and config.config.ui_component (nested from YAML)
  const getCustomComponent = () => {
    return currentStep?.config?.ui_component ||
           currentStep?.config?.config?.ui_component ||
           null;
  };

  // Build template definition from current step
  // Check both paths: config.component (flat) and config.config.component (nested from YAML)
  const getTemplateDefinition = () => {
    const component = currentStep?.config?.component ||
                      currentStep?.config?.config?.component;
    if (!component) return null;

    return {
      template_type: component.template_type,
      ...component,
      // Include actions from step config for button rendering
      actions: currentStep?.config?.actions || currentStep?.config?.config?.actions
    };
  };

  // Handle workflow action from custom components
  const handleWorkflowAction = async (actionName, actionData = {}) => {
    console.log('Workflow action triggered:', actionName, actionData);
    setSubmitting(true);

    try {
      const stepName = currentStep?.name || 'current_step';
      const message = JSON.stringify({
        action: actionName,
        step: stepName,
        data: actionData
      });

      const response = await rfqWorkflowService.continueWorkflow(runId, message);
      console.log('Continue workflow response:', response);

      if (response?.success) {
        // Check if workflow is now completed
        if (response.status === 'completed') {
          Swal.fire({
            icon: 'success',
            title: 'Workflow Completed',
            text: 'The RFQ workflow has been completed successfully.',
            confirmButtonColor: '#3085d6'
          }).then(() => {
            navigate('/rfq-workflow');
          });
          return;
        }

        // Fetch updated status for next step
        await fetchWorkflowStatus();

        // Show success message for the action
        if (actionName === 'send_emails') {
          Swal.fire({
            icon: 'success',
            title: 'Emails Sent',
            text: response.message || 'RFQ emails have been sent to vendors.',
            confirmButtonColor: '#3085d6'
          });
        }
      }
    } catch (err) {
      console.error('Error with workflow action:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to execute action',
        confirmButtonColor: '#d33'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Render custom component based on ui_component
  const renderCustomComponent = (componentName) => {
    const rfqId = workflowRun?.context?.rfq_id;

    const commonProps = {
      runId,
      rfqId,
      workflowContext: workflowRun?.context || {},
      onWorkflowAction: handleWorkflowAction,
      isWorkflowMode: true,
      loading: submitting
    };

    switch (componentName) {
      case 'RFQWorkflowDetail':
        return <RFQWorkflowDetail {...commonProps} />;
      case 'VendorResponsesDashboard':
        return <VendorResponsesDashboard {...commonProps} />;
      case 'BidAnalysisView':
        return <BidAnalysisView {...commonProps} />;
      default:
        return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <MainLayout title="RFQ Workflow">
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </MainLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <MainLayout title="RFQ Workflow">
        <div className="p-6">
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <p className="text-gray-500 mb-4">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/rfq-workflow')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <Button onClick={fetchWorkflowStatus}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Get template definition and custom component
  const templateDefinition = getTemplateDefinition();
  const customComponent = getCustomComponent();

  // Debug logging
  console.log('🔍 RFQWorkflowRun render state:', {
    runId,
    status: workflowRun?.status,
    currentStepName: currentStep?.name,
    currentStepType: currentStep?.type,
    customComponent,
    hasTemplateDefinition: !!templateDefinition,
    stepConfig: currentStep?.config
  });

  // Render workflow completed state
  if (workflowRun?.status === 'completed') {
    const rfqId = workflowRun?.context?.rfq_id;
    return (
      <MainLayout title="RFQ Workflow">
        <div className="p-6">
          <Card>
            <CardContent className="py-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Workflow Completed</h2>
              <p className="text-gray-500 mb-4">The RFQ workflow has been completed successfully.</p>
              <div className="flex gap-3 justify-center">
                {rfqId && (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}`)}
                  >
                    View RFQ Details
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate('/rfq-workflow')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="RFQ Workflow">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/rfq-workflow')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              RFQ Workflow
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Step: {currentStep?.name || 'Loading...'}
            </p>
          </div>
        </div>

        {/* Workflow Content */}
        {customComponent ? (
          // Render custom component (RFQWorkflowDetail, VendorResponsesDashboard, etc.)
          renderCustomComponent(customComponent)
        ) : (
          <Card>
            <CardContent className="pt-6">
              {templateDefinition ? (
                <TemplateRenderer
                  definition={templateDefinition}
                  agentId={19}
                  onClose={handleClose}
                  onNavigate={(url) => navigate(url)}
                  onSubmit={handleFormSubmit}
                  onActionClick={handleActionClick}
                  loading={submitting}
                  workflowContext={workflowRun?.context || {}}
                />
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">
                    Loading step UI...
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default RFQWorkflowRun;
